/**
 * Workshop blob durability adapter — the single seam that decides WHERE a
 * workshop's captured blobs (capture PNGs, Asset Locker uploads) live, so the
 * `useCaptures` / `useAssetLocker` stores don't hard-depend on one backend.
 *
 * Two backends, chosen by env (most-specific wins):
 *   - BFF (`VITE_WORKSHOP_BACKEND=bff`): blobs are documents on the
 *     document-storage BFF under the `workshops` tier at the tier-relative
 *     path `<wsId>/<kind>/<filename>` (the container is the tier). Sign-in
 *     required; managed-identity-backed; re-indexed into mc-workshops.
 *   - GitHub (`isGithubSyncEnabled()`): the legacy Contents-API mirror at
 *     `workshops/<wsId>/<kind>/<filename>` (repo-rooted). No sign-in; PAT-gated.
 *   - Neither: disabled — the stores stay IndexedDB-only (R1 offline posture).
 *
 * The BFF has no optimistic-concurrency token, so `sha` is "" on that path and
 * deletes don't need one; the GitHub path keeps using the blob SHA. Callers
 * treat `sha` as optional and pass back whatever the adapter returned.
 */

import { isGithubSyncEnabled, isWorkshopBffEnabled } from "@/env";
import {
  DocsApiError,
  deleteDoc,
  downloadDoc,
  listDocs,
  uploadDoc,
} from "@/lib/docs-api";
import {
  deleteBlob as ghDeleteBlob,
  downloadBlob as ghDownloadBlob,
  listAssets as ghListAssets,
  listCaptures as ghListCaptures,
  uploadBlob as ghUploadBlob,
  uploadCapture as ghUploadCapture,
} from "@/lib/github-storage";

/** Which per-workshop blob namespace — captures, Asset Locker uploads, or
 *  transcript media (audio/video attached to a session transcript). On the BFF
 *  the kind is just the middle path segment (`<wsId>/<kind>/<file>`); on the
 *  dormant GitHub backend non-capture kinds route through the generic asset
 *  helpers (BFF is the supported mirror target for transcript media). */
export type WorkshopBlobKind = "captures" | "uploads" | "transcripts";

/** Backend-neutral handle to a synced blob (mirrors the GitHub asset shape;
 *  `sha` is "" on the BFF backend, which has no concurrency token). */
export interface SyncedBlob {
  /** The path the entry stores + passes back to download/delete. */
  path: string;
  sha: string;
  size: number;
  updatedAt: string;
}

const TIER = "workshops";

/** Tier-relative blob path for the BFF backend (`<wsId>/<kind>/<file>`). */
const bffPath = (kind: WorkshopBlobKind, workshopId: string, filename: string): string =>
  `${workshopId}/${kind}/${filename}`;

/**
 * Normalise a stored path to the BFF's tier-relative form. New BFF entries
 * already store `<wsId>/<kind>/<file>`, but an entry synced under the old
 * GitHub backend stored the repo-rooted `workshops/<wsId>/<kind>/<file>`; under
 * the `workshops` tier that would double-prefix and 404. Workshop paths always
 * start with `<wsId>/`, never a literal `workshops/`, so stripping a leading
 * `workshops/` is safe.
 */
const toTierRelative = (path: string): string =>
  path.startsWith(`${TIER}/`) ? path.slice(TIER.length + 1) : path;

/** True when blobs sync to a backend at all (BFF or GitHub). */
export function isWorkshopBlobSyncEnabled(): boolean {
  return isWorkshopBffEnabled() || isGithubSyncEnabled();
}

/** Upload (overwrite) a workshop blob to the active backend. */
export async function uploadWorkshopBlob(
  kind: WorkshopBlobKind,
  workshopId: string,
  filename: string,
  blob: Blob,
): Promise<SyncedBlob> {
  if (isWorkshopBffEnabled()) {
    const path = bffPath(kind, workshopId, filename);
    await uploadDoc(TIER, path, blob);
    return { path, sha: "", size: blob.size, updatedAt: new Date().toISOString() };
  }
  const asset =
    kind === "captures"
      ? await ghUploadCapture(workshopId, filename, blob, undefined)
      : await ghUploadBlob(workshopId, filename, blob, undefined);
  return { path: asset.path, sha: asset.sha, size: asset.size, updatedAt: asset.updatedAt };
}

/** List a workshop's blobs of one kind on the active backend. */
export async function listWorkshopBlobs(
  kind: WorkshopBlobKind,
  workshopId: string,
): Promise<SyncedBlob[]> {
  if (isWorkshopBffEnabled()) {
    const blobs = await listDocs(TIER, `${workshopId}/${kind}/`);
    return blobs.map((b) => ({
      path: b.path,
      sha: "",
      size: b.size,
      updatedAt: b.last_modified ?? "",
    }));
  }
  const assets =
    kind === "captures" ? await ghListCaptures(workshopId) : await ghListAssets(workshopId);
  return assets.map((a) => ({ path: a.path, sha: a.sha, size: a.size, updatedAt: a.updatedAt }));
}

/** Download a workshop blob by its stored path. `null` on a 404 soft-miss. */
export async function downloadWorkshopBlob(path: string): Promise<Blob | null> {
  if (isWorkshopBffEnabled()) {
    try {
      return await downloadDoc(TIER, toTierRelative(path));
    } catch (err) {
      if (err instanceof DocsApiError && err.status === 404) return null;
      throw err;
    }
  }
  return await ghDownloadBlob(path);
}

/** Delete a workshop blob. `sha` is required by the GitHub backend only; the
 *  BFF ignores it. A missing sha on the GitHub path is a no-op (can't delete
 *  without the concurrency token). */
export async function deleteWorkshopBlob(path: string, sha?: string): Promise<void> {
  if (isWorkshopBffEnabled()) {
    await deleteDoc(TIER, toTierRelative(path));
    return;
  }
  if (!sha) return;
  await ghDeleteBlob(path, sha);
}
